/**
 * Committee Tabs & Interactions
 *
 * Handles tab switching (URL hash aware), member role filtering,
 * and <details> accordion animations.
 *
 * @package DemocraticServices
 * @since 3.0.0
 */

(function () {
	'use strict';

	/* ──────────────────────────────────────────────
   * 1. Tab Switching
   * ────────────────────────────────────────────── */

	const TAB_TRIGGERS = '[data-tab-trigger]';
	const TAB_PANELS = '[data-tab-panel]';

	/**
   * Activate a tab by name (e.g. 'overview', 'members').
   *
   * @param {string} tabName
   * @param {boolean} focus Whether to move focus to the panel.
   */
	function activateTab(tabName, focus) {
		const triggers = document.querySelectorAll(TAB_TRIGGERS);
		const panels = document.querySelectorAll(TAB_PANELS);
		if (!triggers.length) return;

		triggers.forEach((btn) => {
			const isActive = btn.dataset.tabTrigger === tabName;
			btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
			btn.setAttribute('tabindex', isActive ? '0' : '-1');

			// Active: primary text + thick primary bottom border
			btn.classList.toggle('text-gov-primary', isActive);
			btn.classList.toggle('border-gov-primary', isActive);
			// Inactive: gray text + transparent border + hover accents
			btn.classList.toggle('text-gray-500', !isActive);
			btn.classList.toggle('border-transparent', !isActive);
			btn.classList.toggle('hover:text-gov-primary-dark', !isActive);
			btn.classList.toggle('hover:border-gray-300', !isActive);
		});

		panels.forEach((panel) => {
			const isActive = panel.dataset.tabPanel === tabName;
			panel.classList.toggle('hidden', !isActive);
			// Also remove/add the native hidden attribute so panels that
			// start with the HTML attribute (e.g. petition tabs) toggle
			// visibility correctly.
			if (isActive) {
				panel.removeAttribute('hidden');
			} else {
				panel.setAttribute('hidden', '');
			}
			panel.setAttribute('aria-hidden', !isActive);
			if (isActive && focus) {
				panel.focus({ preventScroll: true });
			}
		});

		// Update URL hash without scroll jump
		if (history.replaceState) {
			history.replaceState(null, '', `#${tabName}`);
		}
	}

	function initTabs() {
		const triggers = document.querySelectorAll(TAB_TRIGGERS);
		if (!triggers.length) return;

		// Click handler (prevent default anchor jump, activate tab instead)
		triggers.forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.preventDefault();
				activateTab(btn.dataset.tabTrigger, true);
			});
		});

		// Keyboard arrow-key navigation within tablist
		const tabList = document.querySelector('[role="tablist"]');
		if (tabList) {
			tabList.addEventListener('keydown', (e) => {
				const orderedTriggers = Array.from(triggers);
				const idx = orderedTriggers.indexOf(document.activeElement);
				if (idx === -1) return;

				let newIdx = idx;
				if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
					newIdx = (idx + 1) % orderedTriggers.length;
				} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
					newIdx = (idx - 1 + orderedTriggers.length) % orderedTriggers.length;
				} else if (e.key === 'Home') {
					newIdx = 0;
				} else if (e.key === 'End') {
					newIdx = orderedTriggers.length - 1;
				} else {
					return; // No matching key
				}

				e.preventDefault();
				orderedTriggers[newIdx].focus();
				activateTab(orderedTriggers[newIdx].dataset.tabTrigger, false);
			});
		}

		// Respect URL hash on page load
		const validTabs = Array.from(triggers).map((t) => t.dataset.tabTrigger);

		/**
     * Resolve a hash string to a valid tab name.
     * Handles both plain slugs ('committees') and legacy
     * 'panel-' prefixed hashes ('panel-committees').
     */
		function resolveHash(raw) {
			const h = raw.replace(/^#/, '');
			if (h && validTabs.includes(h)) return h;
			// Strip legacy 'panel-' prefix
			if (h.startsWith('panel-')) {
				const stripped = h.slice(6);
				if (validTabs.includes(stripped)) return stripped;
			}
			return null;
		}

		// Honour the server-rendered default (the trigger marked
		// aria-selected="true") when no valid hash is present, so the
		// JS-activated tab matches the PHP-rendered visible panel. Without
		// this, the first tab would always win on mount and a server-default
		// panel (e.g. "vote") that is visible on load would be hidden,
		// causing a content flash.
		const preselectedTrigger = Array.from(triggers).find(
			(t) => t.getAttribute('aria-selected') === 'true'
		);
		const serverDefaultTab = preselectedTrigger
			? preselectedTrigger.dataset.tabTrigger
			: validTabs[0];

		const initialTab = resolveHash(window.location.hash);
		activateTab(initialTab || serverDefaultTab, false);

		// Listen for hash changes (e.g. back/forward navigation)
		window.addEventListener('hashchange', () => {
			const tab = resolveHash(window.location.hash);
			if (tab) activateTab(tab, false);
		});
	}

	/* ──────────────────────────────────────────────
   * 2. Member Role Filtering
   * ────────────────────────────────────────────── */

	function initRoleFilter() {
		// Dedicated script committee-member-role-filter.js handles filtering on committee singles.
	}

	/* ──────────────────────────────────────────────
   * 4. Accessible <details> Animation
   * ────────────────────────────────────────────── */

	function initDetailsAnimation() {
		const detailsEls = document.querySelectorAll('.committee-single details');
		detailsEls.forEach((details) => {
			const summary = details.querySelector('summary');
			if (!summary) return;

			summary.addEventListener('click', () => {
				// Let browser handle open/close, just animate the icon
				const icon = summary.querySelector('[data-chevron]');
				if (icon) {
					requestAnimationFrame(() => {
						icon.style.transition = 'transform 0.2s ease';
						icon.style.transform = details.open
							? 'rotate(0deg)'
							: 'rotate(180deg)';
					});
				}
			});
		});
	}

	/* ──────────────────────────────────────────────
   * Init on DOM ready
   * ────────────────────────────────────────────── */

	function init() {
		const isCommittee = document.querySelector('.committee-single');
		const isMeeting = document.querySelector('.meeting-single');
		const isAgenda = document.querySelector('.agenda-single');
		const isMember = document.querySelector('.member-single');
		const isMinutes = document.querySelector('.minutes-single');
		const isPetition = document.querySelector('.petition-single');
		const isElection = document.querySelector('.election-single');
		const isOutsideBody = document.querySelector('.outside-body-single');
		const isConsultation = document.querySelector('.consultation-single');
		const isVote = document.querySelector('.vote-single');
		const isDecision = document.querySelector('.decision-single');

		// Tabs are shared across single pages
		if (
			isCommittee ||
      isMeeting ||
      isAgenda ||
      isMember ||
      isMinutes ||
      isPetition ||
      isElection ||
      isOutsideBody ||
      isConsultation ||
      isVote ||
      isDecision
		) {
			initTabs();
		}

		// Committee-only features
		if (isCommittee) {
			initRoleFilter();
			initDetailsAnimation();
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
